/**
 * Isomorphic, shared by the client that attaches these headers and the server that reads them, so
 * nothing here may import a server-only module. Fetch-based calls ONLY: a document navigation cannot
 * set request headers, so a `pageview` uses the native ones.
 */
import { APP_VERSION_STORAGE_KEY } from './app-version';

export const TIMEZONE_HEADER = 'x-timezone';
export const VIEWPORT_HEADER = 'x-viewport';
export const APP_VERSION_TELEMETRY_HEADER = 'x-app-version';
export const TRACE_ID_HEADER = 'x-trace-id';
export const SESSION_ID_HEADER = 'x-session-id';
export const ROOT_SPAN_ID_HEADER = 'x-root-span-id';

/** sessionStorage, so a new tab is a new session. */
const SESSION_ID_STORAGE_KEY = 'worldcoo:telemetry-session-id';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `Headers.set` (and tRPC) THROW on anything outside printable ASCII, and these ride every call: a bad
 * value must cost one attribute, never the call. `x-app-version` from a corrupted localStorage is the
 * realistic source.
 */
const SAFE_HEADER_VALUE = /^[\x20-\x7e]+$/;

/**
 * These headers are attacker-controlled and land verbatim in durable storage. Every id we mint is a
 * `crypto.randomUUID()`, so requiring that shape bounds the column's width and cardinality and keeps
 * trace-grafting to "can guess a uuid"; a rejected value just leaves the event uncorrelated.
 */
export function asCorrelationId(value: string | null | undefined): string | undefined {
	return value && UUID_RE.test(value) ? value : undefined;
}

declare global {
	interface Window {
		__TRACE_ID?: string;
		__ROOT_SPAN_ID?: string;
		/** The path + search this document was RENDERED for; see `SsrTrace.url` (TEC-3854). */
		__RENDER_URL?: string;
	}
}

let inMemorySessionId: string | undefined;

/**
 * `crypto.randomUUID` exists only in a secure context (absent on a plain-http preview or LAN address),
 * and this must not throw: it runs on the path of every server-function call.
 */
function randomId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return '';
	}
}

/**
 * This tab's visit, across its navigations but never across tabs: a debugging correlation key, not a
 * tracking identifier, and deliberately not a cookie. Falls back to a per-page-load id where storage
 * throws (Safari private mode).
 */
export function getSessionId(): string {
	if (typeof window === 'undefined') return '';
	// Memoized on the happy path too: this runs per outbound call.
	if (inMemorySessionId) return inMemorySessionId;
	try {
		const existing = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
		if (existing) return (inMemorySessionId = existing);
		const minted = randomId();
		if (minted) window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, minted);
		return (inMemorySessionId = minted);
	} catch {
		return (inMemorySessionId = randomId());
	}
}

/** Once per page load: an `Intl.DateTimeFormat` costs ~26us, 4000x a cached string, more on a mid-range phone. */
const timezone = ((): string | undefined => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		// No Intl, or no zone database.
		return undefined;
	}
})();

/** Best-effort: a missing value leaves its attribute null on the event, never fails the request. */
export function telemetryHeaders(): Record<string, string> {
	if (typeof window === 'undefined') return {};
	const headers: Record<string, string> = {};
	const put = (name: string, read: () => string | null | undefined): void => {
		try {
			const value = read();
			if (value && SAFE_HEADER_VALUE.test(value)) headers[name] = value;
		} catch {
			// Intl, window metrics or storage unavailable.
		}
	};
	put(TIMEZONE_HEADER, () => timezone);
	put(VIEWPORT_HEADER, () => `${String(window.innerWidth)}x${String(window.innerHeight)}`);
	// The bundle actually RUNNING here, which a tab left open across a deploy makes differ from the server's.
	// Read fresh: `syncVersionFromHeader` rewrites it mid-session.
	put(APP_VERSION_TELEMETRY_HEADER, () => window.localStorage.getItem(APP_VERSION_STORAGE_KEY));
	// Baked into the document by the SSR render (`__root.tsx` head()), so every call joins the rendering trace.
	put(TRACE_ID_HEADER, () => window.__TRACE_ID);
	put(ROOT_SPAN_ID_HEADER, () => window.__ROOT_SPAN_ID);
	put(SESSION_ID_HEADER, getSessionId);
	return headers;
}
